import { useEffect, useState } from 'react';

import { toast } from 'sonner';

import { InfoCard } from '../../../../../../../../components/info-card';
import { useUpdateFeedback } from '@/api/mutations/useUpdateFeedback';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Textarea } from '@/new/components/shadcn/textarea';

const FREETEXT_LENGTH_LIMIT = 1500;

interface KimFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedbackId: string | null;
}

export function KimFeedbackDialog({ open, onOpenChange, feedbackId }: KimFeedbackDialogProps) {
  const [freeText, setFreeText] = useState('');
  const { updateFeedback } = useUpdateFeedback();

  useEffect(() => {
    if (!open) {
      setFreeText('');
    }
  }, [open]);

  const freeTextOverLimit = freeText.length > FREETEXT_LENGTH_LIMIT;

  const handleSubmit = () => {
    const trimmed = freeText.trim();
    if (!trimmed || !feedbackId || freeTextOverLimit) {
      onOpenChange(false);
      return;
    }
    updateFeedback.mutate(
      { id: feedbackId, freeText: trimmed },
      {
        onSuccess: () => {
          toast.success('Thank you for leaving feedback');
          onOpenChange(false);
        },
        onError: () => {
          toast.error('Failed to submit feedback. Please try again.');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--message-circle" />
            Thanks for leaving feedback
          </DialogTitle>
          <DialogDescription>Optionally tell us more about your rating.</DialogDescription>
        </DialogHeader>

        <InfoCard className="mb-4 gap-2 pb-2.5 text-[13px] text-gray-500">
          Feedback entered below will be shared with the Wexler team to help improve Kim. Do not share sensitive
          information. The content of the thread will remain private.
        </InfoCard>

        <Textarea
          placeholder="Share more about your experience (optional)"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          lengthLimit={FREETEXT_LENGTH_LIMIT}
          className="max-h-72 min-h-28 text-[13px]!"
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Close</Button>
          </DialogClose>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={freeTextOverLimit || updateFeedback.isLoading}
            className="pr-1.5"
          >
            Submit
            <Icon className="lucide--chevron-right -ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
